import type { JourneyDetailDto } from "@/api/endpoints";
import { InfoTooltip } from "@/components/InfoTooltip";
import { fmtWholeBd } from "@/lib/format";
import { PEER_GROUP_TIP, STAGE_LABEL, STAGE_TIP } from "../journeyLabels";
import { segmentWeight, stageViews, type StageView } from "../journeyView";

interface Props {
  journey: JourneyDetailDto;
  /** Opens the tasks panel; the Request processing segment is a button when set. */
  onShowTasks?: () => void;
}

const LEGEND: { cls: string; label: string }[] = [
  { cls: "done", label: "Done" },
  { cls: "moving", label: "Moving" },
  { cls: "stuck", label: "Stuck" },
  { cls: "blocked", label: "Blocked" },
  { cls: "stuckAndBlocked", label: "Stuck + Blocked" },
  { cls: "sentBack", label: "Sent back" },
  { cls: "notStarted", label: "Not started" },
];

/** Below this a peer median is "peers do it at once"; a ratio against it reads as thousands× and says nothing. */
const MEANINGFUL_MEDIAN_BD = 0.05;

function durationLine(v: StageView) {
  const s = v.stage;
  if (s.status === "skipped") return <div className="dur"><span className="med">no stage</span></div>;
  if (s.status === "notStarted") return <div className="dur"><span className="med">{s.peerMedianBd != null ? `median ${s.peerMedianBd.toFixed(2)} bd` : "not started"}</span></div>;
  const ratio = s.peerMedianBd != null && s.bd != null && s.peerMedianBd >= MEANINGFUL_MEDIAN_BD ? s.bd / s.peerMedianBd : null;
  return (
    <div className="dur">
      {fmtWholeBd(s.bd)}{s.status === "current" ? " and counting" : ""}
      {s.peerMedianBd != null && (
        <span className="med">
          {" "}· median {s.peerMedianBd < MEANINGFUL_MEDIAN_BD ? "≈ 0" : s.peerMedianBd.toFixed(2)}
          {ratio != null && ratio > 1.25 ? <> · <span className="ratio">{ratio.toFixed(1)}×</span></> : null}
        </span>
      )}
    </div>
  );
}

/**
 * The six segments of the consultant journey, left to right, sized by elapsed time: Intake,
 * Pre-Verification, Awaiting request, Request processing, WO submit, WO approval. The current segment
 * takes the carrying record's attention colour. The peer medians live in the timing table (the bar's ticks were removed 2026-10-22).
 */
export function LifecycleBar({ journey, onShowTasks }: Props) {
  const views = stageViews(journey);
  return (
    <div className="journey-life" data-testid="lifecycle-bar">
      <div className="journey-life-head">
        <h3>Lifecycle <span className="sub">business days</span><InfoTooltip inline text={PEER_GROUP_TIP} /></h3>
        <div className="journey-legend" aria-label="Legend">
          {LEGEND.map((l) => <span key={l.cls}><i className={`sw ${l.cls}`} />{l.label}</span>)}
        </div>
      </div>
      <div className="journey-stages">
        {views.map((v) => {
          const clickable = v.key === "requestProcessing" && !!onShowTasks && !!journey.request;
          const body = (
            <>
              <div className="bar" aria-hidden="true" />
              <div className="name">
                {STAGE_LABEL[v.key]}
                {v.stage.status === "done" && <span className="material-symbols-rounded" aria-label="done">check</span>}
                {v.stage.status === "current" && <span className="now">· now</span>}
                <InfoTooltip inline text={STAGE_TIP[v.key]} />
              </div>
              {v.facts.map((f, i) => <div key={i} className={`fact${v.stage.status === "current" && i === v.facts.length - 1 ? " hot" : ""}`}>{f}</div>)}
              {durationLine(v)}
              {v.people.length > 0 && (
                <div className="people">
                  {v.people.map((p) => <span key={p.label} className={`av${p.hasTheBall ? " ball" : ""}`} title={p.hasTheBall ? `${p.label} · has the ball` : p.label}><i>{p.initials}</i><span className="lbl">{p.label}</span></span>)}
                </div>
              )}
            </>
          );
          return (
            <div key={v.key} className={`journey-stage ${v.tone}${v.stage.sentBack ? " sentBack" : ""}`} style={{ flex: `${segmentWeight(v)} 1 0` }} data-stage={v.key} data-status={v.stage.status}>
              {clickable ? <button type="button" className="stage-btn" onClick={onShowTasks} aria-label={`${STAGE_LABEL[v.key]}: show the tasks`}>{body}</button> : body}
            </div>
          );
        })}
      </div>
    </div>
  );
}
